import React, { useState } from 'react';
import { motion } from 'framer-motion';

// Import sub-components
import MiniFlowDiagram from './MiniFlowDiagram';
import ConfigCompleteness from './ConfigCompleteness';

import {
  CaretDown,
  CaretRight,
  ChartBar,
  ChatText,
  CheckCircle,
  Clock,
  Cpu,
  Envelope,
  Eye,
  FileAudio,
  FileText,
  GearSix,
  GitBranch,
  Image,
  Lightning,
  Microphone,
  PencilSimple,
  Robot,
  Table,
  Warning,
  WebhooksLogo
} from '@phosphor-icons/react';
/**
 * LivePreviewPanel - Real-time preview of agent configuration
 * 
 * Shows:
 * - Mini flow diagram (visual representation)
 * - Config completeness progress
 * - Agent identity (name, description)
 * - Input/Output types
 * - Capabilities (required + optional)
 * - System prompt preview
 */
const LivePreviewPanel = ({ agentConfig, onConfigUpdate, onSectionFocus }) => {
  const [viewMode, setViewMode] = useState('flow'); // 'flow' | 'details' | 'progress'
  const [expandedSections, setExpandedSections] = useState({
    identity: true,
    io: true,
    capabilities: true,
    prompt: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const inputTypeIcons = {
    text: ChatText,
    file_pdf: FileText,
    file_docx: FileText,
    file_xlsx: Table,
    file_csv: Table,
    file_audio: FileAudio,
    audio: Microphone,
    image: Image,
    webhook: WebhooksLogo,
    webhook_event: WebhooksLogo,
    api_payload: Lightning,
    url: FileText,
    email: Envelope
  };

  const capabilityProfileLabels = {
    'text_agent': { label: 'Text Agent', icon: ChatText, color: 'text-blue-400' },
    'live_voice_s2s_agent': { label: 'Live Voice (S2S)', icon: Microphone, color: 'text-purple-400' },
    'voice_input_agent': { label: 'Voice Input', icon: FileAudio, color: 'text-indigo-400' },
    'voice_output_tts_agent': { label: 'Text-to-Speech', icon: FileAudio, color: 'text-cyan-400' },
    'integration_automation_agent': { label: 'Integration', icon: Lightning, color: 'text-amber-400' }
  };

  const profile = capabilityProfileLabels[agentConfig.capability_profile] || capabilityProfileLabels['text_agent'];
  const ProfileIcon = profile.icon;

  const hasName = agentConfig.name?.trim();
  const hasDescription = agentConfig.description?.trim();
  const hasInputs = agentConfig.accepted_inputs?.length > 0;
  const hasCapabilities = agentConfig.required_capabilities?.length > 0;
  
  const completionScore = [hasName, hasDescription, hasInputs, hasCapabilities].filter(Boolean).length;
  const completionPercent = Math.round((completionScore / 4) * 100);

  const viewModes = [
    { id: 'flow', label: 'Flow', icon: GitBranch },
    { id: 'details', label: 'Details', icon: Eye },
    { id: 'progress', label: 'Progress', icon: ChartBar }
  ];

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-3 border-b border-white/10 bg-[#0a0b0f]">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4 text-indigo-400" />
            <h2 className="font-semibold text-white text-sm">Live Preview</h2>
          </div>
          
          {/* Completion Badge */}
          <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${
            completionPercent === 100 
              ? 'bg-emerald-500/20 text-emerald-400' 
              : completionPercent >= 50
                ? 'bg-amber-500/20 text-amber-400'
                : 'bg-slate-500/20 text-slate-400'
          }`}>
            {completionPercent === 100 ? (
              <CheckCircle className="w-3 h-3" />
            ) : (
              <Warning className="w-3 h-3" />
            )}
            {completionPercent}%
          </div>
        </div>

        {/* View Mode Tabs */}
        <div className="flex items-center gap-1 bg-white/5 p-1 rounded-lg">
          {viewModes.map(mode => (
            <button
              key={mode.id}
              onClick={() => setViewMode(mode.id)}
              className={`
                flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all flex-1 justify-center
                ${viewMode === mode.id 
                  ? 'bg-indigo-500/20 text-indigo-400' 
                  : 'text-slate-500 hover:text-white hover:bg-white/5'
                }
              `}
              data-testid={`preview-mode-${mode.id}`}
            >
              <mode.icon className="w-3 h-3" />
              {mode.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content based on view mode */}
      <div className="flex-1 overflow-y-auto">
        {viewMode === 'flow' && (
          <div className="p-2">
            <MiniFlowDiagram agentConfig={agentConfig} />
          </div>
        )}

        {viewMode === 'progress' && (
          <ConfigCompleteness 
            agentConfig={agentConfig} 
            onSectionClick={onSectionFocus}
          />
        )}

        {viewMode === 'details' && (
          <div className="p-4 space-y-4">
            {/* Empty State - Enhanced */}
            {!hasName && !hasDescription && !hasCapabilities && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex flex-col items-center justify-center text-center py-8"
              >
                <div className="p-4 rounded-2xl bg-gradient-to-br from-indigo-500/10 to-purple-500/10 
                  border border-indigo-500/20 mb-4">
                  <Robot className="w-8 h-8 text-indigo-400" />
                </div>
                <h3 className="text-white font-medium mb-2">No Configuration Yet</h3>
                <p className="text-slate-400 text-sm max-w-xs mb-6">
                  Start describing your agent in the chat to see a live preview of your configuration
                </p>

                {/* Skeleton Preview */}
                <div className="w-full max-w-xs space-y-3 opacity-40">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-lg bg-white/10 animate-pulse" />
                    <div className="h-4 w-24 rounded bg-white/10 animate-pulse" />
                  </div>
                  <div className="h-3 w-full rounded bg-white/10 animate-pulse" />
                  <div className="h-3 w-3/4 rounded bg-white/10 animate-pulse" />
                  <div className="flex gap-2 mt-4">
                    <div className="h-6 w-16 rounded-full bg-white/10 animate-pulse" />
                    <div className="h-6 w-20 rounded-full bg-white/10 animate-pulse" />
                  </div>
                </div>
              </motion.div>
            )}

            {/* Agent Identity Section */}
            {(hasName || hasDescription) && (
              <Section
                title="Agent Identity"
                icon={Robot}
                expanded={expandedSections.identity}
                onToggle={() => toggleSection('identity')}
              >
                <div className="space-y-3">
                  {/* Name */}
                  <div>
                    <label className="text-xs text-slate-500 uppercase tracking-wider">Name</label>
                    <div className="mt-1 text-white font-medium">
                      {agentConfig.name || <span className="text-slate-500 italic">Not set</span>}
                    </div>
                  </div>
                  
                  {/* Description */}
                  <div>
                    <label className="text-xs text-slate-500 uppercase tracking-wider">Description</label>
                    <div className="mt-1 text-slate-300 text-sm">
                      {agentConfig.description || <span className="text-slate-500 italic">Not set</span>}
                    </div>
                  </div>

                  {/* Type Badge */}
                  <div className="flex items-center gap-2 pt-2">
                    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10`}>
                      <ProfileIcon className={`w-4 h-4 ${profile.color}`} />
                      <span className="text-sm text-white">{profile.label}</span>
                    </div>
                  </div>
                </div>
              </Section>
            )}

            {/* Input/Output Section */}
            {hasInputs && (
              <Section
                title="Inputs & Outputs"
                icon={Lightning}
                expanded={expandedSections.io}
                onToggle={() => toggleSection('io')}
              >
                <div className="space-y-4">
                  {/* Accepted Inputs */}
                  <div>
                <label className="text-xs text-slate-500 uppercase tracking-wider mb-2 block">
                  Accepted Inputs
                </label>
                <div className="flex flex-wrap gap-2">
                  {agentConfig.accepted_inputs?.map((input) => {
                    const Icon = inputTypeIcons[input] || ChatText;
                    return (
                      <div
                        key={input}
                        className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-blue-500/10 border border-blue-500/20"
                      >
                        <Icon className="w-3 h-3 text-blue-400" />
                        <span className="text-xs text-blue-300 capitalize">
                          {input.replace(/_/g, ' ').replace('file ', '')}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Required Inputs */}
              {agentConfig.required_inputs?.length > 0 && (
                <div>
                  <label className="text-xs text-slate-500 uppercase tracking-wider mb-2 block">
                    Required Inputs
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {agentConfig.required_inputs.map((input) => {
                      const Icon = inputTypeIcons[input] || ChatText;
                      return (
                        <div
                          key={input}
                          className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-500/10 border border-amber-500/20"
                        >
                          <Icon className="w-3 h-3 text-amber-400" />
                          <span className="text-xs text-amber-300 capitalize">
                            {input.replace(/_/g, ' ').replace('file ', '')}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Expected Outputs */}
              {agentConfig.expected_outputs?.length > 0 && (
                <div>
                  <label className="text-xs text-slate-500 uppercase tracking-wider mb-2 block">
                    Expected Outputs
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {agentConfig.expected_outputs.map((output, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-emerald-500/10 border border-emerald-500/20"
                      >
                        <span className="text-xs text-emerald-300">
                          {typeof output === 'string' ? output : output.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Section>
        )}

        {/* Capabilities Section */}
        {hasCapabilities && (
          <Section
            title="Capabilities"
            icon={Cpu}
            expanded={expandedSections.capabilities}
            onToggle={() => toggleSection('capabilities')}
          >
            <div className="space-y-4">
              {/* Required Capabilities */}
              <div>
                <label className="text-xs text-slate-500 uppercase tracking-wider mb-2 block">
                  Required
                </label>
                <div className="flex flex-wrap gap-2">
                  {agentConfig.required_capabilities?.map((cap) => (
                    <div
                      key={cap}
                      className="px-2.5 py-1 rounded-md bg-purple-500/10 border border-purple-500/20"
                    >
                      <span className="text-xs text-purple-300 capitalize">
                        {cap.replace(/_/g, ' ')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Optional Capabilities */}
              {agentConfig.optional_capabilities?.length > 0 && (
                <div>
                  <label className="text-xs text-slate-500 uppercase tracking-wider mb-2 block">
                    Optional
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {agentConfig.optional_capabilities.map((cap) => (
                      <div
                        key={cap}
                        className="px-2.5 py-1 rounded-md bg-slate-500/10 border border-slate-500/20"
                      >
                        <span className="text-xs text-slate-400 capitalize">
                          {cap.replace(/_/g, ' ')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Section>
        )}

        {/* System Prompt Section */}
        {agentConfig.system_prompt && (
          <Section
            title="System Prompt"
            icon={PencilSimple}
            expanded={expandedSections.prompt}
            onToggle={() => toggleSection('prompt')}
          >
            <div className="p-3 rounded-lg bg-black/30 border border-white/5">
              <pre className="text-xs text-slate-400 whitespace-pre-wrap font-mono">
                {agentConfig.system_prompt.length > 500 && !expandedSections.prompt
                  ? agentConfig.system_prompt.slice(0, 500) + '...'
                  : agentConfig.system_prompt
                }
              </pre>
            </div>
          </Section>
        )}
        </div>
        )}
      </div>
    </div>
  );
};

// Collapsible Section Component
const Section = ({ title, icon: Icon, expanded, onToggle, children }) => (
  <div className="rounded-xl bg-white/[0.02] border border-white/5 overflow-hidden">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between p-4 hover:bg-white/[0.02] transition-colors"
    >
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-slate-400" />
        <span className="font-medium text-white text-sm">{title}</span>
      </div>
      {expanded ? (
        <CaretDown className="w-4 h-4 text-slate-400" />
      ) : (
        <CaretRight className="w-4 h-4 text-slate-400" />
      )}
    </button>
    
    {expanded && (
      <motion.div
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: 'auto' }}
        exit={{ opacity: 0, height: 0 }}
        className="px-4 pb-4"
      >
        {children}
      </motion.div>
    )}
  </div>
);

export default LivePreviewPanel;
